<template>
    <div>
        <vx-auto-suggest-modified
            v-model="searchText"
            ref="navbarSearch"
            v-bind="attrs"
            background-overlay
            hide-group-title
            :select-like="false"
            class="vx-auto-suggest-modified w-full"
            inputClassses="w-full vs-input-no-shadow-focus"
            icon="SearchIcon"
            :autoFocus="autofocus || showFullSearch"
            :data="predictions"
            :selected.sync="selected"
            :show-on-focus="true"
            @input="searchChanged"
            @blur="(e) => $emit('blur', e)"
            @focus="(e) => $emit('focus', e)"
            @closeSearchbar="showFullSearch = false"
        >
            <template v-slot:places="{ suggestion }">
                <slot name="places" :place="suggestion">
                    <div class="flex items-end leading-none py-1">
                        <feather-icon
                            icon="MapPinIcon"
                            svgClasses="h-6 w-6"
                            class="list-icon mr-4"
                        />
                        <span class="">{{ suggestion.description }}</span>
                    </div>
                </slot>
            </template>

            <!-- Loading Slot -->
            <template v-slot:loader>
                <vx-spinner
                    scale="0.6"
                    :type="loaderState == 'listening' ? 'sound' : 'default'"
                ></vx-spinner>
            </template>

            <!-- No Items Slot -->
            <template v-slot:noResult>
                <slot name="noResult">
                    <div class="flex items-center">
                        <feather-icon icon="InfoIcon" svgClasses="h-6 w-6" class="mr-4" />
                        <span>No matching places</span>
                    </div>
                </slot>
            </template>
        </vx-auto-suggest-modified>
        <div class="places_target" ref="places_target"></div>
    </div>
</template>

<script>
import VxAutoSuggestModified from '../../components/generic/VxAutoSuggestModified'

export default {
    name: 'vx-place-search',
    props: {
        value: {
            type: Object
        },
        debounce: {
            type: Number,
            default: 100
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        selectLike: {
            type: Boolean,
            default: true
        },
        showOnFocus: {
            type: Boolean,
            default: false
        },
        displayKey: {
            type: [String, Function]
        },
        getContactInfo: {
            type: Boolean,
            default: false
        },
        getAtmosphereInfo: {
            type: Boolean,
            default: false
        },
        apiKey: {
            type: String,
            required: true
            // default: 'AIzaSyBiJJwgtG_8YqrJxrnKxALs9xQeIvhVVH4'
        }
    },
    components: {
        VxAutoSuggestModified
    },
    data() {
        return {
            icon: '',
            searchText: '',
            showFullSearch: false,
            typingTimeout: null,
            sessiontoken: '',
            types: 'address',
            service: null,
            lastStatus: null,
            prediction_results: [],
            loading_predictions: false,
            needs_new_session: false,
            // Part of base 1.7c cost
            basic_fields: [
                'address_component',
                'adr_address',
                'business_status',
                'formatted_address',
                'geometry',
                'icon',
                'name',
                'photo',
                'place_id',
                'plus_code',
                'type',
                'url',
                'vicinity'
            ],
            // Costs additional 0.3c per session
            contact_fields: [
                'formatted_phone_number',
                'international_phone_number',
                'opening_hours',
                'website'
            ],
            // Costs additional 0.5c per session
            atmos_fields: ['price_level', 'rating', 'review', 'user_ratings_total'],
            test_html: ''
        }
    },
    computed: {
        predictions() {
            return {
                places: {
                    loading: this.loading_predictions,
                    textKey: this.getSearchText,
                    id: 'place_id',
                    data: this.prediction_results
                }
            }
        },
        fieldList() {
            return [
                ...this.basic_fields,
                ...(this.getContactInfo ? this.contact_fields : []),
                ...(this.getAtmosphereInfo ? this.atmos_fields : [])
            ]
        },
        attrs() {
            return { ...this.$attrs, selected: null }
        },
        selected: {
            get() {
                return this.value
            },
            set(val) {
                this.onSelected(val)
            }
        },
        loaderState() {
            if (this.typingTimeout != null) {
                return 'listening'
            } else {
                return 'loading'
            }
        }
    },
    methods: {
        initialize() {
            if (!this.autocompleteService) {
                this.sessiontoken = new window.google.maps.places.AutocompleteSessionToken()
                this.autocompleteService = new window.google.maps.places.AutocompleteService()
            }
        },
        getSearchText(item) {
            if (item) {
                return item.description || item.formatted_address
            }
            return ''
        },
        onSelected({ item }) {
            if (!this.value || this.value.place_id != item.place_id) {
                try {
                    this.$emit('retrieving')
                    var target = this.$refs.places_target
                    const placeService = new window.google.maps.places.PlacesService(target)
                    var payload = {
                        placeId: item.place_id,
                        sessionToken: this.sessiontoken,
                        fields: this.fieldsList
                    }

                    placeService.getDetails(payload, (place) => {
                        this.$emit('input', place)
                    })
                } catch (err) {
                    this.$emit('error', item)
                    this.notifyError(
                        'Missing Record',
                        `Could not find details for ${this.searchText}. ${err}`,
                        8000
                    )
                }
                this.showFullSearch = false
            }
        },
        searchChanged(term) {
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout)
                this.typingTimeout = null
            }

            this.loading_predictions = true
            this.typingTimeout = setTimeout(
                function () {
                    this.typingTimeout = null
                    if (term) {
                        this.fetchPredictions(term)
                    } else {
                        this.prediction_results = []
                        this.loading_predictions = false
                    }
                }.bind(this),
                this.listenFor
            )
        },
        fetchPredictions(term) {
            if (!this.value || this.value.description != term) {
                try {
                    this.initialize()
                    this.autocompleteService.getPlacePredictions(
                        {
                            input: term,
                            sessionToken: this.sessiontoken
                        },
                        (predictions, status) => {
                            this.lastStatus = status
                            this.prediction_results = predictions || []
                            this.loading_predictions = false
                        }
                    )
                } catch (err) {
                    console.error(err)
                }
            }
        },
        focus() {
            this.$refs.navbarSearch.focus()
        }
    },
    mounted() {
        if (!(window.google && window.google.maps && window.google.maps.places)) {
            let recaptchaScript = document.createElement('script')
            recaptchaScript.setAttribute(
                'src',
                `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
            )
            document.head.appendChild(recaptchaScript)
        }
    }
}
</script>

<style scoped>
.vx-auto-suggest-modified ::v-deep .list-icon {
    transition: 0.2s all;
}

.vx-auto-suggest-modified ::v-deep .vx-auto-suggest__current-selected .list-icon {
    margin-right: 1.25rem !important;
    color: rgba(var(--vs-primary), 1);
}
</style>
